export interface CRRFlashQuestion {
  id: string;
  question_fr: string;
  question_en: string;
  answer_fr: string;
  answer_en: string;
  context_fr: string;
  context_en: string;
  simple_fr?: string;
  category: 'general' | 'radiotelephony' | 'asn' | 'phonetic';
}

export const crrFlashQuestions: CRRFlashQuestion[] = [
  // ── General ──
  {
    id: "mmsi",
    question_fr: "Que signifie MMSI ?",
    question_en: "What does MMSI stand for?",
    answer_fr: "Maritime Mobile Service Identity (9 chiffres)",
    answer_en: "Maritime Mobile Service Identity (9 digits)",
    context_fr: "Chaque navire a un MMSI unique, comme un numéro de téléphone en mer.",
    context_en: "Every vessel has a unique MMSI, like a phone number at sea.",
    simple_fr: "C'est le numéro d'identité de ton bateau en mer.",
    category: "general"
  },
  {
    id: "freq-detresse",
    question_fr: "Quelle est la fréquence de détresse VHF ?",
    question_en: "What is the VHF distress frequency?",
    answer_fr: "Canal 16 — 156,800 MHz",
    answer_en: "Channel 16 — 156.800 MHz",
    context_fr: "Le canal 16 est surveillé en permanence par les CROSS et tous les navires.",
    context_en: "Channel 16 is monitored continuously by rescue centres and all vessels.",
    simple_fr: "C'est le canal d'urgence, toujours écouté.",
    category: "general"
  },
  {
    id: "smdsm",
    question_fr: "Que signifie SMDSM ?",
    question_en: "What does GMDSS stand for?",
    answer_fr: "Système Mondial de Détresse et Sécurité en Mer",
    answer_en: "Global Maritime Distress and Safety System",
    context_fr: "Le SMDSM garantit qu'un appel de détresse sera reçu, même loin des côtes.",
    context_en: "GMDSS ensures a distress call will be received, even far from shore.",
    simple_fr: "C'est le système mondial pour les urgences en mer.",
    category: "general"
  },
  {
    id: "portee-vhf",
    question_fr: "Quelle est la portée moyenne d'une VHF portable ?",
    question_en: "What is the average range of a handheld VHF?",
    answer_fr: "Environ 5 milles nautiques",
    answer_en: "About 5 nautical miles",
    context_fr: "La portée dépend de la hauteur d'antenne et des conditions météo.",
    context_en: "Range depends on antenna height and weather conditions.",
    simple_fr: "Ça porte à peu près 5 milles, selon la météo.",
    category: "general"
  },
  {
    id: "crr",
    question_fr: "Que signifie CRR ?",
    question_en: "What does CRR stand for?",
    answer_fr: "Certificat Restreint de Radiotéléphoniste",
    answer_en: "Restricted Radiotelephone Certificate",
    context_fr: "Le CRR est obligatoire pour utiliser une VHF en mer en France.",
    context_en: "The CRR is mandatory to operate a marine VHF in France.",
    simple_fr: "C'est le permis pour utiliser la radio VHF en mer.",
    category: "general"
  },
  {
    id: "anfr",
    question_fr: "Qui délivre le CRR en France ?",
    question_en: "Who issues the CRR in France?",
    answer_fr: "L'ANFR (Agence Nationale des Fréquences)",
    answer_en: "ANFR (National Frequency Agency)",
    context_fr: "L'ANFR gère toutes les fréquences radio en France, y compris maritimes.",
    context_en: "ANFR manages all radio frequencies in France, including maritime.",
    simple_fr: "C'est l'agence qui gère les fréquences radio en France.",
    category: "general"
  },
  {
    id: "canaux-vhf",
    question_fr: "Combien de canaux VHF maritimes existent ?",
    question_en: "How many maritime VHF channels exist?",
    answer_fr: "57 canaux (de 1 à 28 et de 60 à 88)",
    answer_en: "57 channels (1–28 and 60–88)",
    context_fr: "Chaque canal a un usage spécifique : détresse, port, inter-navires, etc.",
    context_en: "Each channel has a specific use: distress, port, inter-ship, etc.",
    category: "general"
  },
  {
    id: "canal-port",
    question_fr: "Quel canal pour les opérations portuaires ?",
    question_en: "Which channel for port operations?",
    answer_fr: "Canal 12 ou canal 14",
    answer_en: "Channel 12 or Channel 14",
    context_fr: "Contactez la capitainerie sur le canal 12 avant d'entrer dans un port.",
    context_en: "Contact the harbour master on channel 12 before entering a port.",
    category: "general"
  },

  // ── Radiotelephony ──
  {
    id: "mayday",
    question_fr: "Quel est le mot de détresse en radiotéléphonie ?",
    question_en: "What is the distress word in radiotelephony?",
    answer_fr: "MAYDAY (répété 3 fois)",
    answer_en: "MAYDAY (repeated 3 times)",
    context_fr: "MAYDAY signifie un danger grave et imminent pour le navire ou les personnes.",
    context_en: "MAYDAY means grave and imminent danger to the vessel or persons.",
    simple_fr: "C'est le mot pour dire 'au secours' à la radio.",
    category: "radiotelephony"
  },
  {
    id: "pan-pan",
    question_fr: "Quel mot signale l'urgence sans danger immédiat ?",
    question_en: "Which word signals urgency without immediate danger?",
    answer_fr: "PAN PAN (répété 3 fois)",
    answer_en: "PAN PAN (repeated 3 times)",
    context_fr: "PAN PAN est utilisé quand il y a un problème sérieux mais pas de danger de mort.",
    context_en: "PAN PAN is used when there's a serious problem but no mortal danger.",
    simple_fr: "C'est pour un problème grave, mais pas mortel.",
    category: "radiotelephony"
  },
  {
    id: "securite",
    question_fr: "Quel mot pour un message de sécurité ?",
    question_en: "Which word for a safety message?",
    answer_fr: "SÉCURITÉ (répété 3 fois)",
    answer_en: "SÉCURITÉ (repeated 3 times)",
    context_fr: "Utilisé pour les avis météo dangereux ou les obstacles à la navigation.",
    context_en: "Used for dangerous weather warnings or navigation hazards.",
    category: "radiotelephony"
  },
  {
    id: "recu",
    question_fr: "Que dire pour confirmer la réception d'un message ?",
    question_en: "What to say to confirm message reception?",
    answer_fr: "REÇU (RECEIVED)",
    answer_en: "RECEIVED",
    context_fr: "Confirme que vous avez bien entendu et compris le message.",
    context_en: "Confirms you have heard and understood the message.",
    category: "radiotelephony"
  },
  {
    id: "chiffre-9",
    question_fr: "Comment épeler le chiffre 9 en radiotéléphonie ?",
    question_en: "How to pronounce the number 9 in radiotelephony?",
    answer_fr: "NOVENINER (NO-VE-NAI-NEUR)",
    answer_en: "NINER (NAI-NER)",
    context_fr: "La prononciation spéciale évite la confusion avec d'autres chiffres.",
    context_en: "Special pronunciation avoids confusion with other numbers.",
    category: "radiotelephony"
  },
  {
    id: "appel-navire",
    question_fr: "Quel canal pour appeler un autre navire ?",
    question_en: "Which channel to call another vessel?",
    answer_fr: "Canal 16, puis basculer sur un canal de travail",
    answer_en: "Channel 16, then switch to a working channel",
    context_fr: "On ne reste jamais sur le 16 pour discuter — il faut libérer le canal.",
    context_en: "Never stay on 16 to chat — you must free the channel.",
    simple_fr: "On appelle sur le 16, puis on change de canal pour parler.",
    category: "radiotelephony"
  },
  {
    id: "roger",
    question_fr: "Que signifie 'ROGER' ?",
    question_en: "What does 'ROGER' mean?",
    answer_fr: "Message reçu et compris",
    answer_en: "Message received and understood",
    context_fr: "ROGER ne veut pas dire 'oui' — seulement que le message est compris.",
    context_en: "ROGER doesn't mean 'yes' — only that the message is understood.",
    category: "radiotelephony"
  },
  {
    id: "out",
    question_fr: "Comment dit-on 'terminé' en VHF ?",
    question_en: "How do you say 'end of communication' on VHF?",
    answer_fr: "OUT (terminé, fin de communication)",
    answer_en: "OUT (end of communication)",
    context_fr: "Ne jamais dire OVER et OUT ensemble — ils sont contradictoires.",
    context_en: "Never say OVER and OUT together — they contradict each other.",
    simple_fr: "OUT = j'ai fini de parler, on raccroche.",
    category: "radiotelephony"
  },
  {
    id: "over",
    question_fr: "Que signifie 'OVER' ?",
    question_en: "What does 'OVER' mean?",
    answer_fr: "À vous — j'attends votre réponse",
    answer_en: "Over to you — I'm waiting for your reply",
    context_fr: "OVER signifie que vous avez fini de parler et attendez une réponse.",
    context_en: "OVER means you've finished speaking and await a reply.",
    category: "radiotelephony"
  },

  // ── ASN / SMDSM ──
  {
    id: "asn",
    question_fr: "Que signifie ASN ?",
    question_en: "What does DSC stand for?",
    answer_fr: "Appel Sélectif Numérique (DSC en anglais)",
    answer_en: "Digital Selective Calling",
    context_fr: "L'ASN envoie automatiquement votre position et identité en cas d'urgence.",
    context_en: "DSC automatically sends your position and identity in an emergency.",
    simple_fr: "C'est l'appel automatique de détresse sur la VHF.",
    category: "asn"
  },
  {
    id: "bouton-distress",
    question_fr: "Quel bouton rouge sur la VHF déclenche l'alerte ASN ?",
    question_en: "Which red button on the VHF triggers the DSC alert?",
    answer_fr: "Le bouton DISTRESS (maintenir 5 secondes)",
    answer_en: "The DISTRESS button (hold for 5 seconds)",
    context_fr: "Le bouton est protégé par un capot pour éviter les déclenchements accidentels.",
    context_en: "The button is protected by a cover to prevent accidental activation.",
    simple_fr: "Appuie 5 secondes sur le gros bouton rouge pour l'alerte.",
    category: "asn"
  },
  {
    id: "canal-70",
    question_fr: "Quel canal est réservé à l'ASN ?",
    question_en: "Which channel is reserved for DSC?",
    answer_fr: "Canal 70 (veille automatique)",
    answer_en: "Channel 70 (automatic watch)",
    context_fr: "Le canal 70 est uniquement pour les données numériques, pas la voix.",
    context_en: "Channel 70 is for digital data only, not voice.",
    category: "asn"
  },
  {
    id: "contenu-asn",
    question_fr: "Que contient un appel de détresse ASN ?",
    question_en: "What does a DSC distress call contain?",
    answer_fr: "MMSI, position GPS, nature de la détresse, heure",
    answer_en: "MMSI, GPS position, nature of distress, time",
    context_fr: "Le GPS connecté à la VHF envoie automatiquement la position.",
    context_en: "GPS connected to the VHF automatically sends the position.",
    category: "asn"
  },
  {
    id: "cross",
    question_fr: "Qui coordonne les secours en mer en France ?",
    question_en: "Who coordinates sea rescue in France?",
    answer_fr: "Les CROSS (Centres Régionaux Opérationnels)",
    answer_en: "CROSS (Regional Operational Centres)",
    context_fr: "Les CROSS surveillent le canal 16 et le canal 70 en permanence.",
    context_en: "CROSS centres monitor channel 16 and channel 70 continuously.",
    simple_fr: "Ce sont les centres de secours en mer en France.",
    category: "asn"
  },
  {
    id: "cross-nombre",
    question_fr: "Combien de CROSS en France métropolitaine ?",
    question_en: "How many CROSS in metropolitan France?",
    answer_fr: "5 CROSS : Gris-Nez, Jobourg, Corsen, Étel, La Garde",
    answer_en: "5 CROSS: Gris-Nez, Jobourg, Corsen, Étel, La Garde",
    context_fr: "Chaque CROSS couvre une zone côtière spécifique de la France.",
    context_en: "Each CROSS covers a specific coastal zone of France.",
    category: "asn"
  },
  {
    id: "zone-a1",
    question_fr: "Quelle zone SMDSM couvre la navigation côtière ?",
    question_en: "Which GMDSS zone covers coastal navigation?",
    answer_fr: "Zone A1 (portée VHF, ~20-30 milles)",
    answer_en: "Zone A1 (VHF range, ~20–30 miles)",
    context_fr: "En zone A1, la VHF suffit pour joindre les secours.",
    context_en: "In zone A1, VHF is sufficient to reach rescue services.",
    category: "asn"
  },

  // ── Phonetic Alphabet ──
  {
    id: "alfa",
    question_fr: "Comment épeler la lettre A en alphabet OTAN ?",
    question_en: "How to spell the letter A in NATO alphabet?",
    answer_fr: "ALFA",
    answer_en: "ALFA",
    context_fr: "L'alphabet OTAN est utilisé pour éviter les erreurs de compréhension.",
    context_en: "The NATO alphabet is used to avoid misunderstandings.",
    category: "phonetic"
  },
  {
    id: "mike",
    question_fr: "Comment épeler la lettre M en alphabet OTAN ?",
    question_en: "How to spell the letter M in NATO alphabet?",
    answer_fr: "MIKE",
    answer_en: "MIKE",
    context_fr: "Exemple : 'Mon indicatif est MIKE ALFA ROMEO INDIA ECHO'.",
    context_en: "Example: 'My call sign is MIKE ALFA ROMEO INDIA ECHO'.",
    category: "phonetic"
  },
  {
    id: "sierra",
    question_fr: "Comment épeler la lettre S en alphabet OTAN ?",
    question_en: "How to spell the letter S in NATO alphabet?",
    answer_fr: "SIERRA",
    answer_en: "SIERRA",
    context_fr: "SIERRA est souvent confondu avec CHARLIE à la radio.",
    context_en: "SIERRA is often confused with CHARLIE on the radio.",
    category: "phonetic"
  },
  {
    id: "zulu",
    question_fr: "Comment épeler la lettre Z en alphabet OTAN ?",
    question_en: "How to spell the letter Z in NATO alphabet?",
    answer_fr: "ZULU",
    answer_en: "ZULU",
    context_fr: "'Heure Zulu' désigne l'heure UTC, utilisée en navigation.",
    context_en: "'Zulu time' refers to UTC time, used in navigation.",
    category: "phonetic"
  },
  {
    id: "zero",
    question_fr: "Comment dit-on le chiffre 0 en radiotéléphonie ?",
    question_en: "How to say the number 0 in radiotelephony?",
    answer_fr: "ZERO (ZÉ-RO)",
    answer_en: "ZERO (ZE-RO)",
    context_fr: "Chaque chiffre a une prononciation spéciale pour être clair à la radio.",
    context_en: "Each digit has a special pronunciation to be clear on radio.",
    category: "phonetic"
  },
  {
    id: "pantafive",
    question_fr: "Comment dit-on le chiffre 5 en radiotéléphonie ?",
    question_en: "How to say the number 5 in radiotelephony?",
    answer_fr: "PANTAFIVE (PAN-TA-FAÏVE)",
    answer_en: "PANTAFIVE (PAN-TA-FIVE)",
    context_fr: "La prononciation 'PANTAFIVE' évite la confusion avec 'NINER' (9).",
    context_en: "The pronunciation 'PANTAFIVE' avoids confusion with 'NINER' (9).",
    category: "phonetic"
  },
];
